/**
 * Runs: a sender's consecutive messages, unbroken by a notice, shown under one
 * avatar. Within a run a burst (under QUIET_GAP_MS) sits tight; a longer pause
 * keeps the run but sets the next message apart (FAME-760), under a time
 * divider whoever sent it (FAME-882).
 */
import type { ThreadMessageModel, ThreadNoticeModel, ThreadRow } from "./Thread"

/** How close two messages must be to belong to the same run. */
const RUN_WINDOW_MS = 5 * 60 * 1000

/** Closer than this sits tight, under the same time divider; longer is a pause (FAME-760). */
const QUIET_GAP_MS = 60 * 1000

export type RunAnnotatedMessage = ThreadMessageModel & {
  isRunStart: boolean
  isRunEnd: boolean
  isTightUnder: boolean
  showsTimeDivider: boolean
}

export type RunAnnotatedRow = RunAnnotatedMessage | ThreadNoticeModel

/** Mark each message with how it sits in its run and its burst. */
export function annotateRuns(rows: ThreadRow[]): RunAnnotatedRow[] {
  let previousMessage: ThreadMessageModel | undefined

  return rows.map((row, index) => {
    if ("notice" in row) return row

    const prev = rows[index - 1]
    const next = rows[index + 1]
    // From the last message, not the row above: notices get no divider.
    const showsTimeDivider =
      !previousMessage ||
      row.timestamp - previousMessage.timestamp > QUIET_GAP_MS
    previousMessage = row

    return {
      ...row,
      isRunStart: !continues(prev, row, RUN_WINDOW_MS),
      isRunEnd: !continues(row, next, RUN_WINDOW_MS),
      isTightUnder: continues(prev, row, QUIET_GAP_MS),
      showsTimeDivider,
    }
  })
}

/** Whether `later` continues `earlier` within `withinMs`: same sender, no notice between. */
function continues(
  earlier: ThreadRow | undefined,
  later: ThreadRow | undefined,
  withinMs: number
): boolean {
  if (!earlier || !later || "notice" in earlier || "notice" in later)
    return false

  return (
    senderKey(earlier) === senderKey(later) &&
    later.timestamp - earlier.timestamp <= withinMs
  )
}

/** Group by side, not handle: our own history carries no handle but is still ours, and
 * handleless peer/direct messages must still group (FAME-760). */
function senderKey(row: ThreadMessageModel): string {
  if (row.isOwn) return "\0own"
  return row.authorHandle ?? "\0peer"
}
